import { Dictionary, startCase } from 'lodash';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  useMutation,
  UseMutationOptions,
  UseMutationResult,
  useQuery,
  useQueryClient,
  UseQueryOptions,
  UseQueryResult,
} from 'react-query';
import { useDispatch } from 'react-redux';
import { CostRate } from 'api/cost-rates';
import { UserPermissionForProject } from 'api/permissions';
import {
  AddProjectUserLiteProps,
  AddTeamToProjectPayload,
  AddTeamToProjectResponse,
  BulkShiftDatesRequest,
  CreateProjectPayload,
  Project,
  ProjectAPI,
  ProjectBulkMergePayload,
  ProjectBulkMergeResult,
  ProjectFieldValue,
  ProjectUserIdentifier,
  ProjectUserLite,
  ProjectUserLiteMembership,
  ProjectUserLiteMutationProps,
  ProjectUsersLiteQueryKey,
  RemoveProjectUserLiteProps,
  RemoveTeamFromProjectPayload,
  RemoveTeamFromProjectResponse,
  UpdateProjectPayload,
  ProjectFinancialsApiData,
  ProjectFinancialsDetails,
  EventSource,
  ShiftTasksPayload,
  DependenciesMap,
  ProjectFinancialsApiQueryParams,
} from 'api/project';
import { FieldValueType, Task } from 'api/tasks';
import { toastService } from 'components/molecules/toast';
import { saveTaskDetailSaga } from 'data/tasks/actions';
import { useTranslate } from 'hooks/use-translation';
import { logger } from 'services';
import { callWorkerWithCleanup } from 'utils/call-worker-with-cleanup';
import { queueMacrotask } from 'utils/macrotask';
import { DateType, ProjectId } from '../api/models';
import { ACCOUNT_META_DATA_QUERY_KEY } from './account';
import {
  getProjectDependencyMapKey,
  getProjectDetailQueryKey,
  getProjectTasksQueryKey,
  getProjectUsersLiteKey,
} from './common';
import {
  tenMinutes,
  ALL_PROJECTS_QUERY_KEY,
  ARCHIVED_PROJECTS_QUERY_KEY,
  PROJECTS_META_QUERY_KEY,
} from './constants';
import { ApiErrorData } from './error';
import { MEMBERS_V2_QUERY_KEY, MEMBERS_V2_MODIFIED_QUERY_KEY } from './members';
import { useHideNonStrategicPartner } from './partner';

const useProjectDetailQuery = (
  projectId: ProjectId,
  options: UseQueryOptions<Project, ApiErrorData, Project, ProjectId> = {},
): UseQueryResult<Project, ApiErrorData> => {
  const { isCurrentUserCustomer, isNonStrategicCompany } =
    useHideNonStrategicPartner();

  return useQuery(
    getProjectDetailQueryKey(projectId),
    () => ProjectAPI.getProjectDetails(projectId[1]),
    {
      staleTime: tenMinutes,
      select: (project) => {
        return {
          ...project,
          partners: isCurrentUserCustomer()
            ? project.partners.filter((x) => !isNonStrategicCompany(x))
            : project.partners,
        };
      },
      ...options,
      refetchOnWindowFocus: false,
    },
  );
};

const useCreateProjectMutation = (
  options: UseMutationOptions<Project, unknown, CreateProjectPayload> = {},
): UseMutationResult<Project, unknown, CreateProjectPayload> => {
  const queryClient = useQueryClient();

  return useMutation<Project, unknown, CreateProjectPayload>(
    (payload) => ProjectAPI.createProject(payload),
    {
      onSuccess: async () => {
        // Todo @Muskaan - Remove below 2 queries once pagination goes out
        await queryClient.invalidateQueries(ALL_PROJECTS_QUERY_KEY);
        await queryClient.invalidateQueries(PROJECTS_META_QUERY_KEY);
        await queryClient.invalidateQueries(ACCOUNT_META_DATA_QUERY_KEY);
        await queryClient.invalidateQueries(MEMBERS_V2_QUERY_KEY);
        await queryClient.refetchQueries(MEMBERS_V2_MODIFIED_QUERY_KEY);
      },
      ...options,
    },
  );
};

const useUpdateProjectMutation = (
  [, projectId]: ProjectId,
  options: UseMutationOptions<Project, unknown, UpdateProjectPayload> = {},
): UseMutationResult<Project, unknown, UpdateProjectPayload> => {
  const queryClient = useQueryClient();

  return useMutation<Project, unknown, UpdateProjectPayload>(
    (payload) => ProjectAPI.updateProject(projectId, payload),
    {
      onSuccess: async (response) => {
        await queryClient.setQueryData(
          getProjectDetailQueryKey(ProjectId(projectId)),
          response,
        );
        // Todo @Muskaan - Remove below 2 queries once pagination goes out
        await queryClient.invalidateQueries(ALL_PROJECTS_QUERY_KEY);
        await queryClient.invalidateQueries(PROJECTS_META_QUERY_KEY);
      },
      ...options,
    },
  );
};

type ExportProjectAuditLogPayload = Pick<Project, 'projectId'>;

const useExportProjectAuditLogMutation = (): UseMutationResult<
  { downloadUrl: string },
  unknown,
  ExportProjectAuditLogPayload
> => {
  return useMutation<
    { downloadUrl: string },
    unknown,
    ExportProjectAuditLogPayload
  >('export_project_audit_logs', ({ projectId }) =>
    ProjectAPI.exportProjectAuditLogs(projectId),
  );
};

type DeleteProjectPayload = Pick<Project, 'projectId'>;

const useDeleteProjectMutation = (
  onSuccess?: () => void,
): UseMutationResult<void, unknown, DeleteProjectPayload> => {
  const queryClient = useQueryClient();

  return useMutation<void, unknown, DeleteProjectPayload>(
    ({ projectId }) => ProjectAPI.deleteProject(projectId),
    {
      onSuccess: async () => {
        // Todo @Muskaan - Remove below 2 queries once pagination goes out
        await queryClient.refetchQueries(ALL_PROJECTS_QUERY_KEY);
        await queryClient.invalidateQueries(PROJECTS_META_QUERY_KEY);
        await queryClient.invalidateQueries(ACCOUNT_META_DATA_QUERY_KEY);
        if (onSuccess) {
          onSuccess();
        }
      },
    },
  );
};

type ArchiveProjectPayload = Pick<Project, 'projectId'>;

const useArchiveProjectMutation = (
  onSuccess?: () => void,
): UseMutationResult<void, unknown, ArchiveProjectPayload> => {
  const queryClient = useQueryClient();

  return useMutation<void, unknown, ArchiveProjectPayload>(
    ({ projectId }) => ProjectAPI.archiveProject(projectId),
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries(ARCHIVED_PROJECTS_QUERY_KEY);
        // Todo @Muskaan - Remove below 2 queries once pagination goes out
        await queryClient.invalidateQueries(ALL_PROJECTS_QUERY_KEY);
        await queryClient.invalidateQueries(PROJECTS_META_QUERY_KEY);
        await queryClient.invalidateQueries(ACCOUNT_META_DATA_QUERY_KEY);
        if (onSuccess) {
          onSuccess();
        }
      },
    },
  );
};
const useUnarchiveProjectMutation = (): UseMutationResult<
  void,
  unknown,
  ArchiveProjectPayload
> => {
  const queryClient = useQueryClient();

  return useMutation<void, unknown, ArchiveProjectPayload>(
    ({ projectId }) => ProjectAPI.unarchiveProject(projectId),
    {
      onSuccess: () => {
        // Queue to next tick so that onSuccess will work fine without causing a re-render
        queueMacrotask(async () => {
          await queryClient.invalidateQueries(ARCHIVED_PROJECTS_QUERY_KEY);
          // Todo @Muskaan - Remove below 2 queries once pagination goes out
          await queryClient.invalidateQueries(ALL_PROJECTS_QUERY_KEY);
          await queryClient.invalidateQueries(PROJECTS_META_QUERY_KEY);
          await queryClient.invalidateQueries(ACCOUNT_META_DATA_QUERY_KEY);
        });
      },
    },
  );
};

const useProjectBulkMergeMutation = (
  projectId: ProjectId,
): UseMutationResult<Project, unknown, ProjectBulkMergePayload[]> => {
  const queryClient = useQueryClient();

  return useMutation<Project, unknown, ProjectBulkMergePayload[]>(
    (payload) => ProjectAPI.bulkMerge(projectId[1], payload),
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries(
          getProjectDetailQueryKey(projectId),
        );
        await queryClient.invalidateQueries(getProjectTasksQueryKey(projectId));
      },
    },
  );
};

const useProjectBulkMergePreviewMutation = ([
  ,
  projectId,
]: ProjectId): UseMutationResult<
  ProjectBulkMergeResult[],
  unknown,
  ProjectBulkMergePayload[]
> => {
  return useMutation<
    ProjectBulkMergeResult[],
    unknown,
    ProjectBulkMergePayload[]
  >((payload) => ProjectAPI.bulkMergePreview(projectId, payload));
};

export type ProjectGridPayload = {
  projectId: number;
  payload: UpdateProjectPayload;
};

const getAllProjectsQueryUpdater = (newProjects: Project[]) => {
  const updater = (oldData: Project[] | undefined): any => {
    logger.log('trying to update based on edit success status', newProjects);
    if (!oldData) {
      return oldData;
    }

    return oldData.map((project) => {
      const predicateByProjectId = ({ projectId }) =>
        project.projectId === projectId;
      const updatedProject = newProjects.find(predicateByProjectId) || project;

      return updatedProject;
    });
  };

  return updater;
};

type UpdateProject = (newProject: Project) => void;

const useToUpdateProjectsQuery = (queryKey: string[]): UpdateProject => {
  const queryClient = useQueryClient();

  const [projectById, setProjectById] = useState<Record<string, Project>>({});
  const updateQueryClient = useRef<(x0: Project[]) => void>();

  useEffect(() => {
    const updateProject = (newProjects: Project[]) => {
      queryClient.setQueryData(
        queryKey,
        getAllProjectsQueryUpdater(newProjects),
      );
      setProjectById({});
    };

    updateQueryClient.current = updateProject;
  }, [queryClient, queryKey]);

  const updateProjectToQueryClient = (newProject: Project) => {
    const updatedProjectById = {
      ...projectById,
      [newProject.projectId]: newProject,
    };

    setProjectById(updatedProjectById);
    updateQueryClient.current?.(Object.values(updatedProjectById));
  };

  return updateProjectToQueryClient;
};

const patchNativeProjectFields = (
  project: Project,
  payload: UpdateProjectPayload,
) => {
  const [keyChanged] = Object.keys(payload);
  const [value] = Object.values(payload);

  return {
    ...project,
    [keyChanged]: value,
  };
};

const patchProjectByPayload = (
  project: Project,
  payload: UpdateProjectPayload,
): Project => {
  if (!payload) {
    return project;
  }

  if (!payload.fields) {
    return patchNativeProjectFields(project, payload);
  }
  const newProject: Project = JSON.parse(JSON.stringify(project));
  const [payloadField] = payload.fields as ProjectFieldValue<FieldValueType>[];

  const predicateByFieldId = (field: ProjectFieldValue<FieldValueType>) =>
    field.fieldId === payloadField.fieldId;
  const existingProjectField = newProject.fields.find(predicateByFieldId);

  if (!existingProjectField) {
    newProject.fields.push(payloadField);

    return newProject;
  }
  existingProjectField.fieldValue = payloadField.fieldValue;

  return newProject;
};

const useProjectGridToUpdate = ({
  onError,
}: {
  onError: ({ project, meta }: { project?: Project; meta?: Project }) => void;
}): UseMutationResult<
  Project,
  unknown,
  ProjectGridPayload,
  {
    fallback: {
      project: Project | undefined;
      meta: Project | undefined;
    };
  }
> => {
  const queryClient = useQueryClient();
  const updateProjectQuery = useToUpdateProjectsQuery(ALL_PROJECTS_QUERY_KEY);
  const updateProjectMeta = useToUpdateProjectsQuery(PROJECTS_META_QUERY_KEY);
  const translate = useTranslate();

  return useMutation(
    ({ projectId, payload }: ProjectGridPayload) =>
      ProjectAPI.updateProject(projectId, payload),
    {
      onSuccess: (project: Project) => {
        const { projectId } = project;

        updateProjectQuery(project);
        queryClient.invalidateQueries(
          getProjectDetailQueryKey(ProjectId(projectId)),
        );
      },
      onMutate: async ({ projectId, payload }: ProjectGridPayload) => {
        const projectQueryKey = getProjectDetailQueryKey(ProjectId(projectId));

        const predicateById = (project: Project) =>
          project.projectId === projectId;

        const previous =
          queryClient.getQueryData<Project>(projectQueryKey) ||
          queryClient
            .getQueryData<Project[]>(ALL_PROJECTS_QUERY_KEY)
            ?.find(predicateById);
        const previousMeta = queryClient
          .getQueryData<Project[]>(PROJECTS_META_QUERY_KEY)
          ?.find(predicateById);

        if (previous) {
          const newProject = patchProjectByPayload(previous, payload);

          updateProjectQuery(newProject);
        }
        if (previousMeta && !payload.fields) {
          const newMeta = patchNativeProjectFields(previousMeta, payload);

          updateProjectMeta(newMeta);
        }

        return {
          fallback: {
            project: previous,
            meta: previousMeta,
          },
        };
      },
      onError: (
        err: Record<string, Record<string, unknown>>,
        project,
        context,
      ) => {
        const { fallback } = context || {};

        if (fallback?.project) {
          updateProjectQuery(fallback.project);
        }
        if (fallback?.meta) {
          updateProjectMeta(fallback.meta);
        }
        onError({ meta: fallback?.meta, project: fallback?.project });
        const fieldName = startCase(Object.keys(project.payload)?.[0]);
        const projectName = fallback?.project?.projectName;

        const errorTitle = translate('project.error.unauth.update.title');
        const errorMsg = translate('project.error.unauth.update.message', {
          fieldName,
          projectName,
          bold: (chunks) => <strong>{chunks}</strong>,
        });

        if (err instanceof Error && err?.request?.status === 403) {
          toastService({
            title: errorTitle,
            message: errorMsg,
          }).error();
        }
      },
      retry: false,
    },
  );
};

const addTeamsAPI = (
  projectId: number,
  teams: AddTeamToProjectPayload[],
): Promise<AddTeamToProjectResponse[]> => ProjectAPI.addTeams(projectId, teams);

const useAddTeamsToProjectMutation = (
  options: UseMutationOptions<
    AddTeamToProjectResponse[],
    unknown,
    { projectId: number; teamIds: number[] }
  > = {},
): UseMutationResult<
  AddTeamToProjectResponse[],
  unknown,
  { projectId: number; teamIds: number[] }
> =>
  useMutation(
    async ({ projectId, teamIds }) => {
      const team = await addTeamsAPI(
        projectId,
        teamIds.map((teamId) => ({ teamId })),
      );
      const worker = callWorkerWithCleanup();

      worker.workerApi
        .addProjectToTeams({
          projectId,
          teamIds,
        })
        .catch((err) => {
          worker.cleanup();
          logger.error(
            `Error occured while adding projectId to team. teamIds: ${teamIds}`,
            err,
          );
        })
        .finally(() => {
          worker.cleanup();
        });

      return team;
    },

    options,
  );

const removeTeamsAPI = (
  projectId: number,
  teams: RemoveTeamFromProjectPayload[],
): Promise<RemoveTeamFromProjectResponse[]> =>
  ProjectAPI.removeTeams(projectId, teams);

const useRemoveTeamsFromProjectMutation = (
  options: UseMutationOptions<
    RemoveTeamFromProjectResponse[],
    unknown,
    { projectId: number; teamIds: number[] }
  > = {},
): UseMutationResult<
  RemoveTeamFromProjectResponse[],
  unknown,
  { projectId: number; teamIds: number[] }
> =>
  useMutation(
    ({ projectId, teamIds }) =>
      removeTeamsAPI(
        projectId,
        teamIds.map((teamId) => ({ teamId })),
      ),
    options,
  );

const useProjectUsersLiteQuery = (
  queryOptions: UseQueryOptions<
    Dictionary<ProjectUserLite[]>,
    unknown,
    Dictionary<ProjectUserLite[]>,
    ProjectUsersLiteQueryKey
  > = {},
): UseQueryResult<Dictionary<ProjectUserLite[]>> =>
  useQuery(
    getProjectUsersLiteKey(),
    () =>
      ProjectAPI.getAllProjectUsersLite().then((data) => {
        const lookup: Dictionary<ProjectUserLite[]> = {};

        data.forEach(({ projectId, members }) => {
          lookup[projectId] = (lookup[projectId] ?? []).concat(members);
        });

        return lookup;
      }),
    {
      cacheTime: 0,
      refetchOnWindowFocus: false, // ? This was handled globally right ?
      ...queryOptions,
    },
  );

const useProjectUsersLiteForProjectQuery = (
  projectId: number,
  queryOptions: UseQueryOptions<
    ProjectUserLiteMembership,
    unknown,
    ProjectUserLiteMembership,
    ProjectUsersLiteQueryKey
  > = {},
): UseQueryResult<ProjectUserLiteMembership> =>
  useQuery(
    getProjectUsersLiteKey(projectId),
    () => ProjectAPI.getProjectUsersLiteForProject(projectId),
    {
      refetchOnWindowFocus: false,
      ...queryOptions,
    },
  );

const filterExistingProjectUsers = (
  oldState: Dictionary<ProjectUserLite[]> | undefined = {},
  projectId: number,
  projectUser: ProjectUserLite,
) => {
  const {
    user: { userId },
    role: projectUserRole,
    referenceTeam: projectUserReferenceTeam,
  } = projectUser;

  return (
    oldState[projectId]?.filter(
      ({ user, role, referenceTeam }) =>
        !(
          user.userId === userId &&
          role?.roleId === projectUserRole?.roleId &&
          referenceTeam?.teamId === projectUserReferenceTeam?.teamId
        ),
    ) ?? []
  );
};

const useAddProjectUserLiteToQueryState = (): ((
  projectId: number,
  projectUser: ProjectUserLite,
) => void) => {
  const queryClient = useQueryClient();

  return useCallback(
    (projectId: number, projectUser: ProjectUserLite) => {
      queryClient.setQueryData<Dictionary<ProjectUserLite[]>>(
        getProjectUsersLiteKey(),
        (state = {}) => ({
          ...state,
          [projectId]: filterExistingProjectUsers(
            state,
            projectId,
            projectUser,
          ).concat(projectUser),
        }),
      );
    },
    [queryClient],
  );
};

const useRemoveProjectUserLiteFromQueryState = (): ((
  projectId: number,
  projectUser: ProjectUserLite,
) => void) => {
  const queryClient = useQueryClient();

  return useCallback(
    (projectId: number, projectUser: ProjectUserLite) => {
      queryClient.setQueryData<Dictionary<ProjectUserLite[]>>(
        getProjectUsersLiteKey(),
        (state = {}) => ({
          ...state,
          [projectId]: filterExistingProjectUsers(
            state,
            projectId,
            projectUser,
          ),
        }),
      );
    },
    [queryClient],
  );
};

const useAddProjectUsersLiteMutation = (
  options: UseMutationOptions<
    AddProjectUserLiteProps,
    unknown,
    {
      projectId: number;
      projectUsers: ProjectUserIdentifier[];
      source?: EventSource;
      assignedToProjectMember?: boolean;
    }
  > = {},
): UseMutationResult<
  AddProjectUserLiteProps,
  unknown,
  {
    projectId: number;
    projectUsers: ProjectUserIdentifier[];
    source?: EventSource;
    assignedToProjectMember?: boolean;
  }
> =>
  useMutation(
    ({
      projectId,
      projectUsers,
      source,
      assignedToProjectMember,
    }: ProjectUserLiteMutationProps) =>
      ProjectAPI.addProjectUsersLite(projectId, {
        members: projectUsers,
        source,
        assignedToProjectMember,
      }),
    options,
  );

const useRemoveProjectUsersLiteMutation = (
  options: UseMutationOptions<
    RemoveProjectUserLiteProps,
    unknown,
    {
      projectId: number;
      projectUsers: ProjectUserIdentifier[];
      source?: EventSource;
      assignedToProjectMember?: boolean;
    }
  > = {},
): UseMutationResult<
  RemoveProjectUserLiteProps,
  unknown,
  {
    projectId: number;
    projectUsers: ProjectUserIdentifier[];
    source?: EventSource;
    assignedToProjectMember?: boolean;
  }
> =>
  useMutation(
    ({
      projectId,
      projectUsers: usersReplacingTeams,
      source,
    }: ProjectUserLiteMutationProps) =>
      ProjectAPI.removeProjectUsersLite(projectId, {
        members: usersReplacingTeams,
        source,
      }),
    options,
  );
const useAddPartnersToProjectMutation = (
  options: UseMutationOptions<
    unknown,
    unknown,
    { projectId: number; partners: number[] }
  > = {},
): UseMutationResult<
  unknown,
  unknown,
  { projectId: number; partners: number[] }
> => {
  const queryClient = useQueryClient();
  const translate = useTranslate();

  return useMutation(
    ({ projectId, partners }) => ProjectAPI.addPartners(projectId, partners),
    {
      onSuccess: (_, params) => {
        queryClient.invalidateQueries(
          getProjectDetailQueryKey(ProjectId(params.projectId)),
        );
        toastService({
          title: translate('project.invitation.add_partners.success.title', {
            count: params.partners.length,
          }),
          message: '',
        }).success();
      },
      onError: (_, params) => {
        toastService({
          title: translate('project.invitation.add_partners.error.title', {
            count: params.partners.length,
          }),
          message: '',
        }).error();
      },
      ...options,
    },
  );
};
const useRemovePartnersToProjectMutation = (
  options: UseMutationOptions<
    unknown,
    unknown,
    { projectId: number; partners: number[] }
  > = {},
): UseMutationResult<
  unknown,
  unknown,
  { projectId: number; partners: number[] }
> => {
  const queryClient = useQueryClient();

  return useMutation(
    ({ projectId, partners }) => ProjectAPI.removePartners(projectId, partners),
    {
      onSuccess: (_, params) => {
        queryClient.invalidateQueries(
          getProjectDetailQueryKey(ProjectId(params.projectId)),
        );
      },
      ...options,
    },
  );
};

export const useBulkShiftDatesMutation = (
  options: UseMutationOptions<
    void,
    unknown,
    {
      projectId: number;
      payload: BulkShiftDatesRequest;
    }
  > = {},
) =>
  useMutation(
    ({
      projectId,
      payload,
    }: {
      projectId: number;
      payload: BulkShiftDatesRequest;
    }) => ProjectAPI.bulkShiftDates(projectId, payload),
    options,
  );

export type ProjectFinancialsQueryKey = ['project-financials', number, boolean];

export const getProjectFinancialsQueryKey = (
  projectId: number,
  includeSubscriptionMeta = false,
): ProjectFinancialsQueryKey => [
  'project-financials',
  projectId,
  includeSubscriptionMeta,
];

export const useProjectFinancialsQuery = (
  projectId: number,
  includeSubscriptionMeta?: boolean,
  subscriptionPeriodId?: DateType,
  queryParams?: ProjectFinancialsApiQueryParams,
  queryOptions: UseQueryOptions<
    ProjectFinancialsApiData,
    unknown,
    ProjectFinancialsApiData,
    ProjectFinancialsQueryKey
  > = {},
): UseQueryResult<ProjectFinancialsApiData> =>
  useQuery(
    getProjectFinancialsQueryKey(projectId, includeSubscriptionMeta),
    () =>
      ProjectAPI.getProjectFinancials({
        projectId,
        includeSubscriptionMeta,
        subscriptionPeriodId,
        queryParams,
      }),
    {
      ...queryOptions,
      cacheTime: 0,
    },
  );

export type ProjectPermissionsQueryKey = ['project-permissions', number];

export const getProjectPermissionsQueryKey = (
  projectId: number,
): ProjectPermissionsQueryKey => ['project-permissions', projectId];

export const useProjectPermissionsQuery = (
  projectId: number,
  queryOptions: UseQueryOptions<
    UserPermissionForProject,
    unknown,
    UserPermissionForProject,
    ProjectPermissionsQueryKey
  > = {},
): UseQueryResult<UserPermissionForProject> =>
  useQuery(
    getProjectPermissionsQueryKey(projectId),
    () => ProjectAPI.getProjectPermissionsForCurrentUser(projectId),
    {
      ...queryOptions,
    },
  );

export const RateCardsProjectSettingsQueryKey = (
  projectId: number,
): [string, number] => ['rate-cards-project-settings', projectId];

export const useProjectsBudgetAndRateSettingsQuery = (
  projectId: number,
  queryOptions: UseQueryOptions<
    ProjectFinancialsDetails,
    unknown,
    ProjectFinancialsDetails,
    [string, number]
  > = {},
): UseQueryResult<ProjectFinancialsDetails> =>
  useQuery(
    RateCardsProjectSettingsQueryKey(projectId),
    () => ProjectAPI.getProjectBudgetSettings(projectId),
    {
      ...queryOptions,
      cacheTime: 0,
    },
  );

const ProjectUserTeamsQueryKey = (projectId: number) => [
  'project-user-teams',
  projectId,
];

export const useProjectUserTeamsQuery = (
  projectId: number,
  queryOptions: UseQueryOptions<
    ProjectUserLiteMembership,
    unknown,
    ProjectUserLiteMembership,
    (string | number)[]
  > = {},
): UseQueryResult<ProjectUserLiteMembership> =>
  useQuery(
    ProjectUserTeamsQueryKey(projectId),
    () => ProjectAPI.getProjectUsersLiteForProject(projectId),
    {
      ...queryOptions,
    },
  );

const ProjectUserTeamsCostRates = (projectId: number) => [
  'project-user-team-cost-rates',
  projectId,
];

export const useProjectUserTeamsCostRatesQuery = (
  projectId: number,
  queryOptions: UseQueryOptions<
    CostRate[],
    unknown,
    CostRate[],
    (string | number)[]
  > = {},
): UseQueryResult<CostRate[]> =>
  useQuery(
    ProjectUserTeamsCostRates(projectId),
    () => ProjectAPI.getProjectUserTeamsCostRates(projectId),
    {
      ...queryOptions,
      cacheTime: 0,
    },
  );

export const useProjectsBudgetAndRateSettingsMutation = (
  projectId: number,
  options: UseMutationOptions<
    ProjectFinancialsDetails,
    unknown,
    ProjectFinancialsDetails
  > = {},
): UseMutationResult<
  ProjectFinancialsDetails,
  unknown,
  ProjectFinancialsDetails
> =>
  useMutation(
    (payload: ProjectFinancialsDetails) =>
      ProjectAPI.updateProjectBudgetSettings(projectId, payload),
    {
      ...options,
    },
  );

const useShiftTasksMutation = (
  projectId: number,
): UseMutationResult<unknown, unknown, ShiftTasksPayload, unknown> => {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();

  return useMutation((payload) => ProjectAPI.shiftTasks(projectId, payload), {
    onSuccess: async (response: ShiftTasksPayload) => {
      if (response) {
        response.updatedTasks.forEach((task) =>
          dispatch(saveTaskDetailSaga(task as Task)),
        );
      }
      await queryClient.invalidateQueries(
        getProjectDetailQueryKey(ProjectId(projectId)),
      );
      await queryClient.invalidateQueries(
        getProjectTasksQueryKey(ProjectId(projectId)),
      );

      await queryClient.invalidateQueries(
        getProjectDependencyMapKey(projectId),
      );

      return response;
    },
  });
};
const useGetDependenciesMap = (
  projectId: number,
): UseQueryResult<DependenciesMap | undefined> =>
  useQuery(
    getProjectDependencyMapKey(projectId),
    () => ProjectAPI.getDependenciesMap(projectId),
    {
      cacheTime: 0,
    },
  );

export {
  useProjectDetailQuery,
  useCreateProjectMutation,
  useUpdateProjectMutation,
  useExportProjectAuditLogMutation,
  useDeleteProjectMutation,
  useArchiveProjectMutation,
  useUnarchiveProjectMutation,
  useProjectBulkMergeMutation,
  useProjectBulkMergePreviewMutation,
  useProjectGridToUpdate,
  useAddTeamsToProjectMutation,
  useRemoveTeamsFromProjectMutation,
  useProjectUsersLiteQuery,
  useProjectUsersLiteForProjectQuery,
  useAddProjectUserLiteToQueryState,
  useRemoveProjectUserLiteFromQueryState,
  useAddProjectUsersLiteMutation,
  useRemoveProjectUsersLiteMutation,
  useAddPartnersToProjectMutation,
  useRemovePartnersToProjectMutation,
  useShiftTasksMutation,
  useGetDependenciesMap,
};
